import { Scale } from 'lucide-react';
import { type ReactNode, type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskView } from '@apm/contracts';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';
import { TaskEstimateDialog, TaskEstimateSummary, estimateSourceOf } from './task-estimate-body';

/**
 * Ô story point trên board, Gantt và danh sách việc — người dùng yêu cầu 2026-11-02:
 * "có thêm một chỗ nữa để show số SP của mỗi task. Cái nào có hay chưa có cũng show.
 * Khi bấm vào vị trí đó thì sẽ show rõ là họ tự điền hay estimate qua một task cũ.
 * Trong đó cũng có nút estimate lại".
 *
 * Luôn hiện: "5 SP", hoặc "– SP" viền đứt khi chưa có. SP ra từ so sánh với việc cũ
 * thì có biểu tượng cân; chênh quá 50% (`needsReview`) thì tô vàng. Bấm mở popover
 * `TaskEstimateSummary` — chỉ số SP, giờ ước lượng, cách ước lượng (việc cũ nào, SP của
 * nó, chênh %), và nút Ước lượng / Ước lượng lại (so với việc cũ hoặc tự điền).
 *
 * Nằm trong thẻ board (bấm thẻ = mở drawer, kéo thẻ = đổi cột) nên chặn `click` và
 * `pointerdown` lan lên: bấm ô SP chỉ mở popover.
 *
 * ⚠️ Không nhận asOf: thuần trình bày `TaskView` người gọi truyền vào.
 */
export function StoryPointBadge({
  task,
  canEdit,
  className,
  variant = 'pill',
}: {
  readonly task: TaskView;
  /** Có nút Ước lượng lại. Thiếu ⇒ theo `task.canEdit` của server. */
  readonly canEdit?: boolean;
  readonly className?: string;
  /**
   * `pill` (danh sách, Gantt): viên bo tròn có nền màu. `chip` (thẻ board): cùng hình
   * với chip mức ưu tiên và hạn trên thẻ — người dùng yêu cầu 2026-11-02 ("làm nó giống
   * với mức ưu tiên và ngày trong thẻ"). Hình nền chip do thẻ truyền qua `className`
   * (`CHIP_BASE`); ở đây chỉ thêm vạch màu trái: xanh = so với việc cũ, vàng = chênh
   * quá 50%, trong suốt = tự điền / chưa có.
   */
  readonly variant?: 'pill' | 'chip';
}): ReactNode {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [estimating, setEstimating] = useState(false);
  const sp = task.storyPoints;
  const source = estimateSourceOf(task);
  const fromReference = source === 'reference';
  const review = task.estimateReference?.needsReview === true && fromReference;
  const stop = (event: SyntheticEvent): void => event.stopPropagation();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="story-point-badge"
          aria-label={`${sp === null ? t('actualTime.spNone') : t('actualTime.spValue', { value: sp })} — ${t('actualTime.badgeTitle')}`}
          title={t('actualTime.badgeTitle')}
          onClick={stop}
          onPointerDown={stop}
          onKeyDown={stop}
          className={
            variant === 'chip'
              ? cn(
                  className,
                  'shrink-0 border-l-[3px] pl-1.5 tabular-nums',
                  'cursor-pointer transition-colors hover:bg-muted-foreground/20',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                  sp === null && 'text-muted-foreground',
                  review && 'text-rag-amber',
                )
              : cn(
                  'inline-flex shrink-0 items-center gap-0.5 rounded-full border px-1.5 py-px text-[11px] font-semibold tabular-nums leading-4 hover:brightness-95',
                  sp === null
                    ? 'border-dashed border-border font-normal text-muted-foreground hover:bg-muted'
                    : review
                      ? 'border-rag-amber/50 bg-rag-amber/10 text-rag-amber'
                      : 'border-primary/40 bg-primary/10 text-primary',
                  className,
                )
          }
          style={
            variant === 'chip'
              ? {
                  borderLeftColor: review
                    ? 'var(--color-rag-amber)'
                    : fromReference
                      ? 'var(--color-primary)'
                      : 'transparent',
                }
              : undefined
          }
        >
          {fromReference ? <Scale aria-hidden className="size-3" /> : null}
          {sp === null ? t('actualTime.badgeNone') : t('actualTime.spValue', { value: sp })}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 max-w-[calc(100vw-2rem)]"
        onClick={stop}
        onPointerDown={stop}
        data-testid="story-point-popover"
      >
        <p className="mb-1 truncate text-xs text-muted-foreground">{task.title}</p>
        <TaskEstimateSummary
          task={task}
          canEdit={canEdit ?? task.canEdit === true}
          onEstimate={() => {
            setOpen(false);
            setEstimating(true);
          }}
        />
      </PopoverContent>
      {estimating ? (
        // Hộp portal ra `body` nhưng sự kiện React vẫn nổi theo cây component —
        // không chặn thì bấm trong hộp là mở drawer / bắt đầu kéo thẻ phía sau.
        <span className="contents" onClick={stop} onPointerDown={stop}>
          <TaskEstimateDialog task={task} onClose={() => setEstimating(false)} />
        </span>
      ) : null}
    </Popover>
  );
}
